/**
 * User Profile & Avatar Helper Utility
 *
 * Rules:
 * - If user logged in with SSO (Google), use their Google profile picture.
 * - If user logged in with email/password (or has no custom avatar), generate a default avatar.
 */

// IBM Carbon / TeamOne friendly color palette for deterministic generated avatars
const AVATAR_PALETTE = [
  '0F62FE', // IBM Blue 60
  '0043CE', // IBM Blue 70
  '6929C4', // Purple 60
  '0072C3', // Cyan 60
  '005D5D', // Teal 70
  '8A3FFC', // Magenta / Violet
  '1192E8', // Sky 50
  '343A40', // Slate 80
];

/**
 * Deterministically picks a theme color hex from the user's name or email.
 */
export function getAvatarColorHex(seed?: string): string {
  if (!seed) return AVATAR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[index];
}

/**
 * Generates a standard default avatar URL for a user given their name and/or email.
 */
export function getGeneratedAvatarUrl(name?: string, email?: string): string {
  const displayName = (name || email?.split('@')[0] || 'User').trim();
  const cleanName = encodeURIComponent(displayName || 'User');
  const bgHex = getAvatarColorHex(displayName || email || 'User');
  return `https://ui-avatars.com/api/?name=${cleanName}&background=${bgHex}&color=ffffff&bold=true&size=128`;
}

/**
 * Resolves the effective avatar URL for a user object or name string.
 * - Checks for SSO / Google profile picture / custom avatar
 * - Falls back to default generated avatar for email/password or missing picture
 */
export function getUserAvatarUrl(
  userOrName?: string | { name?: string; email?: string; avatar?: string; avatarUrl?: string; picture?: string; photoUrl?: string; photo_url?: string; avatar_url?: string } | null,
  fallbackEmail?: string
): string {
  if (!userOrName) {
    return getGeneratedAvatarUrl('User', fallbackEmail);
  }

  if (typeof userOrName === 'string') {
    const trimmed = userOrName.trim();
    // Check if the string is already a direct URL
    if (
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('data:') ||
      trimmed.startsWith('/') ||
      trimmed.startsWith('blob:')
    ) {
      return trimmed;
    }
    // If it's a display name, generate avatar
    return getGeneratedAvatarUrl(trimmed, fallbackEmail);
  }

  const explicitAvatar =
    userOrName.avatar ||
    userOrName.avatarUrl ||
    userOrName.picture ||
    userOrName.photoUrl ||
    userOrName.photo_url ||
    userOrName.avatar_url;

  if (
    explicitAvatar &&
    typeof explicitAvatar === 'string' &&
    (explicitAvatar.startsWith('http://') ||
      explicitAvatar.startsWith('https://') ||
      explicitAvatar.startsWith('data:') ||
      explicitAvatar.startsWith('/') ||
      explicitAvatar.startsWith('blob:'))
  ) {
    return explicitAvatar;
  }

  return getGeneratedAvatarUrl(userOrName.name, userOrName.email || fallbackEmail);
}

/**
 * Extracts clean uppercase first letter initial for fallback badges
 */
export function getUserInitials(name?: string, email?: string): string {
  if (name && name.trim()) {
    return name.trim()[0].toUpperCase();
  }
  if (email && email.includes('@')) {
    return email.split('@')[0][0].toUpperCase();
  }
  return 'U';
}

